import { InformationCircleIcon } from "@heroicons/react/outline";
import { LockClosedIcon } from "@heroicons/react/solid";
import MuiTooltip from "@mui/material/Tooltip";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getExcludedLayers,
  MANDATORY_CONTEXT_LAYERS,
  OPTIONAL_CONTEXT_LAYERS,
  OptionalContextLayer,
  setExcludedLayers,
} from "../../util/slopifyContextPrefs";

interface ContextPopoverProps {
  contentId: number | undefined;
  onChange?: (excluded: OptionalContextLayer[]) => void;
}

const LayerInfoIcon = ({ message }: { message: string }) => (
  <MuiTooltip title={message} arrow placement="left">
    <div className="w-3.5 h-3.5 text-gray-400 cursor-pointer shrink-0 ml-auto">
      <InformationCircleIcon className="w-full h-full" />
    </div>
  </MuiTooltip>
);

export const ContextPopover = ({ contentId, onChange }: ContextPopoverProps) => {
  const { t } = useTranslation();
  const [excluded, setExcluded] = useState<OptionalContextLayer[]>(() =>
    typeof contentId === "number" ? getExcludedLayers(contentId) : [],
  );

  useEffect(() => {
    if (typeof contentId !== "number") {
      setExcluded([]);
      return;
    }
    setExcluded(getExcludedLayers(contentId));
  }, [contentId]);

  const toggle = useCallback(
    (layer: OptionalContextLayer) => {
      if (typeof contentId !== "number") return;
      setExcluded((prev) => {
        const next = prev.includes(layer) ? prev.filter((l) => l !== layer) : [...prev, layer];
        setExcludedLayers(contentId, next);
        onChange?.(next);
        return next;
      });
    },
    [contentId, onChange],
  );

  const disabled = typeof contentId !== "number";

  return (
    <div
      className="text-[11px] text-gray-600"
      role="group"
      aria-label={t("general.assistantScenarIa.context.title") as string}
    >
      <div className="font-medium text-gray-700 mb-1.5">
        {t("general.assistantScenarIa.context.title")}
      </div>
      <ul className="space-y-1">
        {MANDATORY_CONTEXT_LAYERS.map((layer) => (
          <li key={layer} className="flex items-center gap-2 opacity-60">
            <LockClosedIcon className="w-3 h-3 text-gray-400 shrink-0" />
            <span className="flex-1 truncate">
              {t(`general.assistantScenarIa.context.layers.${layer}`)}
            </span>
            <LayerInfoIcon
              message={t(`general.assistantScenarIa.context.tooltips.${layer}`) as string}
            />
          </li>
        ))}
        {OPTIONAL_CONTEXT_LAYERS.map((layer) => {
          const isOn = !excluded.includes(layer);
          return (
            <li key={layer} className="flex items-center gap-2">
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-green-500"
                  checked={isOn}
                  disabled={disabled}
                  onChange={() => toggle(layer)}
                  aria-label={t(`general.assistantScenarIa.context.layers.${layer}`) as string}
                />
              </label>
              <span className={`flex-1 truncate ${isOn ? "" : "line-through text-gray-400"}`}>
                {t(`general.assistantScenarIa.context.layers.${layer}`)}
              </span>
              <LayerInfoIcon
                message={t(`general.assistantScenarIa.context.tooltips.${layer}`) as string}
              />
            </li>
          );
        })}
      </ul>
      <div className="text-[10px] text-gray-400 mt-2">
        {t("general.assistantScenarIa.context.note")}
      </div>
    </div>
  );
};

export default ContextPopover;
